import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { handleTokenResponse, refreshToken } from '../../actions/auth';
import Common from '../../api/Common';

export class AuthProvider extends React.PureComponent {
    static propTypes = {
        children: PropTypes.element,
        handleTokenResponse: PropTypes.func.isRequired,
        refreshToken: PropTypes.func.isRequired,
        accessToken: PropTypes.string,
        expiresAt: PropTypes.number
    };

    static childContextTypes = {
        isAuthenticated: PropTypes.func
    };

    refreshing = null;
    pendingXhrQueue = [];
    requestsCount = 0;

    constructor(props) {
        super(props);

        this.xhrOpen = XMLHttpRequest.prototype.open;
        this.xhrSend = XMLHttpRequest.prototype.send;
        this.enhanceXMLHttpRequest();
    }

    componentWillUnmount() {
        // restore XMLHttpRequest#open()
        XMLHttpRequest.prototype.open = this.xhrOpen;
        XMLHttpRequest.prototype.send = this.xhrSend;
    }

    enhanceXMLHttpRequest() {
        /* eslint consistent-this: ["error", "that"] */
        const that = this;
        const attachBehaviors = this.attachBehaviors.bind(this);
        const open = this.xhrOpen;
        const send = this.xhrSend;
        XMLHttpRequest.prototype.open = function (...args) {
            const [, url] = args; // [method, url, async, user, password]
            this._heUrl = url;
            // Should be executed before "open" to catch the event "readystatechange"
            that.countRequests(this);
            return open.apply(this, args);
        };

        XMLHttpRequest.prototype.send = function (...sendArgs) {
            const url = this._heUrl;

            const doSend = () => {
                attachBehaviors(this, url);
                return send.apply(this, sendArgs);
            };

            // On bloque ici si le token est expiré
            if (that.hasToken() && that.isTokenExpired()) {
                // On attend le refresh avant d'envoyer la requête
                if (!that.refreshing) {
                    that.refreshing = that.props.refreshToken().finally(() => {
                        that.refreshing = null;
                    });
                }
                that.refreshing.then(doSend).catch(() => {
                    if (typeof this.abort === 'function') {
                        this.abort();
                    }
                });
                return;
            }

            return doSend();
        };
    }

    attachBehaviors(xhr, url) {
        if (!url) {
            return;
        }
        this.injectAuthorizationHeader(xhr, url);
        this.handleTokenResponse(xhr, url);
    }

    countRequests(xhr) {
        xhr.addEventListener('readystatechange', () => {
            if (xhr.readyState == XMLHttpRequest.OPENED) {
                this.requestsCount++;
            } else if (xhr.readyState == XMLHttpRequest.DONE) {
                this.requestsCount--;
            }
        });
    }

    injectAuthorizationHeader(xhr, url) {
        // Inject Authorization header in API requests
        if (
            (this.isRelativeUrl(url) || url.startsWith(Common.getApiUri())) &&
            this.hasToken() &&
            (xhr.hasOwnProperty('auth') === false || Boolean(xhr.auth) === true)
        ) {
            xhr.setRequestHeader('Authorization', `Bearer ${this.props.accessToken}`);
        }
    }

    handleTokenResponse(xhr, url) {
        // Add token response handler if it is an auth requests
        if (url.match('/auth/signin')) {
            xhr.addEventListener(
                'load',
                () => {
                    this.props.handleTokenResponse(xhr);
                },
                false
            );
        }
    }

    isRelativeUrl(url) {
        const r = new RegExp('^(?:[a-z]+:)?//', 'i');
        return !r.test(url);
    }

    hasToken() {
        return Boolean(this.props.accessToken);
    }

    isTokenExpired() {
        const SAFE_DELAY_SECONDS = 15;
        return new Date().getTime() >= (this.props.expiresAt - SAFE_DELAY_SECONDS) * 1000;
    }

    isAuthenticated() {
        return this.hasToken();
    }

    getChildContext() {
        return {
            isAuthenticated: this.isAuthenticated
        };
    }

    render() {
        if (this.props.children && this.isAuthenticated()) {
            return React.Children.only(this.props.children);
        }
        return null;
    }
}

const mapStateToProps = (state) => ({
    accessToken: state.auth.accessToken,
    expiresAt: state.auth.expiresAt || null
});

const mapDispatchToProps = (dispatch) => ({
    handleTokenResponse: bindActionCreators(handleTokenResponse, dispatch),
    refreshToken: bindActionCreators(refreshToken, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(AuthProvider);
